import { useBusinessInfo } from '@va/dashboard/shared/account-settings';
import { useTranslate } from '@va/localization';
import { BusinessInfoType } from '@va/types/account';
import { SelectDropdown, SelectDropdownOption, TextInput } from '@va/ui/design-system';
import { getCountryOptions } from '@va/util/helpers';
import { useFormik } from 'formik';
import { useCallback } from 'react';
import { BusinessInfoCompletion } from './BusinessInfoCompletion';

const formFieldNames = {
  firstName: 'firstName',
  lastName: 'lastName',
  companyName: 'companyName',
  country: 'country',
  vat: 'vat',
  businessType: 'businessType',
  businessSize: 'businessSize',
  businessSector: 'businessSector',
  companyWebsite: 'companyWebsite',
  state: 'state',
  street: 'street',
  zipcode: 'zipcode',
  city: 'city',
};

type BusinessInfoFormProps = {
  id?: string;
  form: ReturnType<typeof useFormik<BusinessInfoType>>;
};

export const BusinessInfoForm = ({ id, form }: BusinessInfoFormProps) => {
  const translate = useTranslate();
  const {
    businessSectorOptions,
    businessSizeOptions,
    businessTypeOptions,
    getSelectedBusinessSector,
    getSelectedBusinessSize,
    getSelectedBusinessType,
  } = useBusinessInfo();

  const { handleSubmit, handleChange, values, errors, setFieldValue } = form;

  const countryOptions: SelectDropdownOption<string>[] = getCountryOptions(translate);
  const getSelectedCountry = useCallback(
    (countryCode: string) => {
      return countryOptions.find((option) => option.value === countryCode);
    },
    [countryOptions],
  );

  return (
    <form id={id} onSubmit={handleSubmit} className='grid sm:grid-cols-1 grid-cols-2 gap-1.5 p-3'>
      <BusinessInfoCompletion className='sm:col-span-1 col-span-2 mb-1.5 text-center' businessInfo={values} />
      <TextInput
        required
        error={errors.companyName as string}
        name={formFieldNames.companyName}
        label={translate('form.placeholders.companyName')}
        wrapperClassName='sm:col-span-1 col-span-2'
        onChange={handleChange}
        value={values.companyName}
        clearField={() => {
          setFieldValue(formFieldNames.companyName, '');
        }}
      />
      <TextInput
        required
        error={errors.firstName as string}
        name={formFieldNames.firstName}
        label={translate('form.placeholders.firstName')}
        onChange={handleChange}
        value={values.firstName}
        clearField={() => {
          setFieldValue(formFieldNames.firstName, '');
        }}
      />
      <TextInput
        required
        error={errors.lastName as string}
        name={formFieldNames.lastName}
        label={translate('form.placeholders.lastName')}
        onChange={handleChange}
        value={values.lastName}
        clearField={() => {
          setFieldValue(formFieldNames.lastName, '');
        }}
      />
      <TextInput
        error={errors.companyWebsite as string}
        name={formFieldNames.companyWebsite}
        label={translate('form.placeholders.companyWebsite')}
        onChange={handleChange}
        value={values.companyWebsite}
        clearField={() => {
          setFieldValue(formFieldNames.companyWebsite, '');
        }}
      />
      <TextInput
        error={errors.vat as string}
        name={formFieldNames.vat}
        label={translate('form.placeholders.vatIdentificationNumber')}
        onChange={handleChange}
        value={values.vat}
        clearField={() => {
          setFieldValue(formFieldNames.vat, '');
        }}
      />
      <SelectDropdown
        label={translate('dropdown.businessData.businessType.placeholder')}
        selectedOption={getSelectedBusinessType(values.businessType)}
        dropdownOptions={businessTypeOptions}
        onChange={(option) => {
          setFieldValue(formFieldNames.businessType, option.value);
        }}
        error={errors.businessType}
      />
      <SelectDropdown
        label={translate('dropdown.businessData.businessSize.placeholder')}
        selectedOption={getSelectedBusinessSize(values.businessSize)}
        dropdownOptions={businessSizeOptions}
        onChange={(option) => {
          setFieldValue(formFieldNames.businessSize, option.value);
        }}
        error={errors.businessSize}
      />
      <SelectDropdown
        label={translate('dropdown.businessData.businessSector.placeholder')}
        selectedOption={getSelectedBusinessSector(values.businessSector)}
        dropdownOptions={businessSectorOptions}
        onChange={(option) => {
          setFieldValue(formFieldNames.businessSector, option.value);
        }}
        error={errors.businessSector}
      />
      <TextInput
        error={errors.street as string}
        name={formFieldNames.street}
        label={translate('form.placeholders.streetAndNumber')}
        onChange={handleChange}
        value={values.street}
        clearField={() => {
          setFieldValue(formFieldNames.street, '');
        }}
      />
      <TextInput
        error={errors.zipcode as string}
        name={formFieldNames.zipcode}
        label={translate('form.placeholders.zipCode')}
        onChange={handleChange}
        value={values.zipcode}
        clearField={() => {
          setFieldValue(formFieldNames.zipcode, '');
        }}
      />
      <TextInput
        error={errors.city as string}
        name={formFieldNames.city}
        label={translate('form.placeholders.city')}
        onChange={handleChange}
        value={values.city}
        clearField={() => {
          setFieldValue(formFieldNames.city, '');
        }}
      />
      <TextInput
        error={errors.state as string}
        name={formFieldNames.state}
        label={translate('form.placeholders.state')}
        onChange={handleChange}
        value={values.state}
        clearField={() => {
          setFieldValue(formFieldNames.state, '');
        }}
      />
      <SelectDropdown
        label={translate('form.placeholders.country')}
        selectedOption={getSelectedCountry(values.country)}
        dropdownOptions={countryOptions}
        onChange={(option) => {
          setFieldValue(formFieldNames.country, option.value);
        }}
        error={errors.country as string}
        required
        comparisonType='match'
      />
    </form>
  );
};
